import { Download, FileText, Plus, Search, Settings, Trash2 } from 'lucide-react';
import * as React from 'react';
import toast from 'react-hot-toast';
import {
  emptyTemplateAvailability,
  TemplateAvailabilityFields,
  templateAvailabilityLabel,
  type TemplateAvailabilityValue,
} from '@/components/drafts/TemplateAvailabilityFields';
import { TemplateUploadDropArea } from '@/components/drafts/TemplateUploadDropArea';
import {
  isTemplateMetadataValid,
  templateDescriptionLength,
  TEMPLATE_DESCRIPTION_MAX_LENGTH,
  templateMatcherPromptLength,
  TEMPLATE_MATCHER_PROMPT_MIN_LENGTH,
} from '@/components/drafts/templateValidation';
import Loading from '@/components/shared/Loading';
import { FileTypeCell } from '@/components/ui/column-builders';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import {
  fileTypeFacetOptionsForValues,
  SimpleTableFacetedFilter,
} from '@/components/ui/simple-table-faceted-filter';
import { SimpleTableActionsDropdown } from '@/components/ui/simple-table-actions-dropdown';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import {
  createAdminDraftDocumentTemplate,
  deleteAdminDraftDocumentTemplate,
  type DraftDocumentTemplate,
  downloadAdminDraftDocumentTemplate,
  updateAdminDraftDocumentTemplate,
  useAdminDraftDocumentTemplates,
} from '@/hooks/useDraftDocuments';
import { apiErrorMessage } from '@/lib/api-client';
import { AdminTabContainer } from './ShellTabs';

const titleFromTemplateFile = (fileName: string) =>
  fileName.replace(/\.docx$/i, '').replace(/[^\w .-]+/g, ' ').replace(/\s+/g, ' ').trim();

const templateDescription = (template: DraftDocumentTemplate) =>
  template.description || template.when_to_use || 'No description';

const TEMPLATE_FILE_TYPE = 'Word';

const AdminTemplateFileTypeHeader = ({
  options,
  selectedValues,
  onSelectedValuesChange,
}: {
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
}) => (
  <div className="flex justify-center">
    <SimpleTableFacetedFilter
      title="File type"
      options={options}
      selectedValues={selectedValues}
      onSelectedValuesChange={onSelectedValuesChange}
    />
  </div>
);

const AdminTemplateTable = ({
  canDeleteTemplate = () => true,
  canEditTemplate = () => true,
  deletingTemplateGuid,
  downloadingTemplateGuid,
  emptyTitle = 'No templates found.',
  onDelete,
  onDownload,
  onEdit,
  templates,
}: {
  canDeleteTemplate?: (template: DraftDocumentTemplate) => boolean;
  canEditTemplate?: (template: DraftDocumentTemplate) => boolean;
  deletingTemplateGuid: string | null;
  downloadingTemplateGuid: string | null;
  emptyTitle?: string;
  onDelete: (template: DraftDocumentTemplate) => void;
  onDownload: (template: DraftDocumentTemplate) => void;
  onEdit: (template: DraftDocumentTemplate) => void;
  templates: DraftDocumentTemplate[];
}) => {
  const [fileTypeFilter, setFileTypeFilter] = React.useState<string[]>([]);
  const fileTypeOptions = React.useMemo(() => (
    fileTypeFacetOptionsForValues(templates.length ? [TEMPLATE_FILE_TYPE] : [])
  ), [templates]);
  const visibleTemplates = React.useMemo(() => (
    fileTypeFilter.length && !fileTypeFilter.includes(TEMPLATE_FILE_TYPE)
      ? []
      : templates
  ), [fileTypeFilter, templates]);

  return (
    <div>
      <div
        className="max-w-full rounded-md border transition-all duration-150"
        style={{ width: '100%' }}
      >
        <Table className="table-fixed" style={{ minWidth: '688px' }}>
          <colgroup>
            <col style={{ width: '64px' }} />
            <col />
            <col style={{ width: '64px' }} />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">
                <AdminTemplateFileTypeHeader
                  options={fileTypeOptions}
                  selectedValues={fileTypeFilter}
                  onSelectedValuesChange={setFileTypeFilter}
                />
              </TableHead>
              <TableHead>Template name</TableHead>
              <TableHead className="w-16">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleTemplates.length ? (
              visibleTemplates.map((template) => {
                const actions = [
                  {
                    label: 'Download',
                    icon: Download,
                    loading: downloadingTemplateGuid === template.template_guid,
                    loadingLabel: 'Downloading...',
                    onSelect: () => onDownload(template),
                  },
                  ...(canEditTemplate(template)
                    ? [{
                      label: 'Configure template',
                      icon: Settings,
                      onSelect: () => onEdit(template),
                    }]
                    : []),
                  ...(canDeleteTemplate(template)
                    ? [{
                      label: 'Delete',
                      icon: Trash2,
                      destructive: true,
                      loading: deletingTemplateGuid === template.template_guid,
                      loadingLabel: 'Deleting...',
                      disabled: !!deletingTemplateGuid,
                      onSelect: () => onDelete(template),
                    }]
                    : []),
                ];

                return (
                  <TableRow key={template.template_guid}>
                    <TableCell className="w-16">
                      <FileTypeCell
                        fileName={`${template.title || 'template'}.docx`}
                        originalFileType="Word"
                        isDraft={false}
                        documentId={template.template_guid}
                      />
                    </TableCell>
                    <TableCell className="min-w-0">
                      <div className="min-w-0" title={template.title}>
                        <p className="truncate font-medium">{template.title}</p>
                        <p className="line-clamp-2 text-xs text-gray-500">
                          {templateDescription(template)}
                        </p>
                        <p className="mt-1 text-[11px] font-medium text-slate-500">
                          {templateAvailabilityLabel(template)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="w-16">
                      <SimpleTableActionsDropdown
                        label={`Actions for ${template.title}`}
                        actions={actions}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="h-48 text-center">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500">
                      <FileText className="h-5 w-5" />
                    </div>
                    <p className="text-sm font-medium text-slate-800">{emptyTitle}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-2 px-2 text-sm text-muted-foreground">
        {visibleTemplates.length === 200
          ? 'Showing first 200'
          : `${visibleTemplates.length} ${visibleTemplates.length === 1 ? 'template' : 'templates'}`}
      </div>
    </div>
  );
};

const TemplateFormFields = ({
  description,
  descriptionInputId,
  availability,
  disabled = false,
  file,
  fileInputId,
  onDescriptionChange,
  onFileChange,
  onTitleChange,
  onAvailabilityChange,
  onWhenToUseChange,
  showFile = false,
  title,
  titleInputId,
  whenToUse,
  whenToUseInputId,
}: {
  description: string;
  descriptionInputId: string;
  availability?: TemplateAvailabilityValue;
  disabled?: boolean;
  file?: File | null;
  fileInputId?: string;
  onDescriptionChange: (value: string) => void;
  onFileChange?: (file: File | null) => void;
  onTitleChange: (value: string) => void;
  onAvailabilityChange?: (value: TemplateAvailabilityValue) => void;
  onWhenToUseChange: (value: string) => void;
  showFile?: boolean;
  title: string;
  titleInputId: string;
  whenToUse: string;
  whenToUseInputId: string;
}) => (
  <>
    {showFile && fileInputId ? (
      <div className="grid gap-2">
        <label htmlFor={fileInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
          File
        </label>
        <TemplateUploadDropArea
          inputId={fileInputId}
          file={file ?? null}
          onFileChange={(nextFile) => onFileChange?.(nextFile)}
          disabled={disabled}
        />
      </div>
    ) : null}
    <div className="grid gap-2">
      <label htmlFor={titleInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
        Title
      </label>
      <Input
        id={titleInputId}
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
        placeholder="Template title"
      />
    </div>
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={descriptionInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Description
        </label>
        <span className="text-xs text-slate-500">
          {templateDescriptionLength(description)}/{TEMPLATE_DESCRIPTION_MAX_LENGTH}
        </span>
      </div>
      <Textarea
        id={descriptionInputId}
        value={description}
        onChange={(event) => onDescriptionChange(event.target.value)}
        maxLength={TEMPLATE_DESCRIPTION_MAX_LENGTH}
        required
        className="min-h-20 text-sm"
        placeholder="Short description"
      />
    </div>
    {availability && onAvailabilityChange ? (
      <TemplateAvailabilityFields
        value={availability}
        onChange={onAvailabilityChange}
        disabled={disabled}
      />
    ) : null}
    <div className="grid gap-2">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor={whenToUseInputId} className="text-xs font-medium uppercase tracking-wide text-slate-500">
          Matcher prompt
        </label>
        <span className="text-xs text-slate-500">
          {templateMatcherPromptLength(whenToUse)}/{TEMPLATE_MATCHER_PROMPT_MIN_LENGTH} min
        </span>
      </div>
      <Textarea
        id={whenToUseInputId}
        value={whenToUse}
        onChange={(event) => onWhenToUseChange(event.target.value)}
        minLength={TEMPLATE_MATCHER_PROMPT_MIN_LENGTH}
        required
        className="min-h-20 text-sm"
        placeholder="How Case Partner should decide when to use this template"
      />
    </div>
  </>
);

const AdminTemplateUploadDialog = ({
  onOpenChange,
  onUploaded,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onUploaded: () => Promise<void>;
  open: boolean;
}) => {
  const fileInputId = React.useId();
  const titleInputId = React.useId();
  const descriptionInputId = React.useId();
  const whenToUseInputId = React.useId();
  const [file, setFile] = React.useState<File | null>(null);
  const [title, setTitle] = React.useState('');
  const [titleTouched, setTitleTouched] = React.useState(false);
  const [description, setDescription] = React.useState('');
  const [whenToUse, setWhenToUse] = React.useState('');
  const [availability, setAvailability] = React.useState<TemplateAvailabilityValue>(emptyTemplateAvailability());
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) return;
    setFile(null);
    setTitle('');
    setTitleTouched(false);
    setDescription('');
    setWhenToUse('');
    setAvailability(emptyTemplateAvailability());
    setIsSaving(false);
  }, [open]);

  const handleFileChange = React.useCallback((nextFile: File | null) => {
    setFile(nextFile);
    if (nextFile && !titleTouched) {
      setTitle(titleFromTemplateFile(nextFile.name));
    }
  }, [titleTouched]);

  const handleSubmit = React.useCallback(async () => {
    if (!file) {
      toast.error('Choose a DOCX template file');
      return;
    }
    if (!isTemplateMetadataValid(description, whenToUse)) {
      toast.error('Add a description and a matcher prompt of at least 50 characters');
      return;
    }

    setIsSaving(true);
    try {
      await createAdminDraftDocumentTemplate({
        scope: 'tenant',
        title: title.trim() || titleFromTemplateFile(file.name) || 'Untitled template',
        file,
        description: description.trim() || null,
        when_to_use: whenToUse.trim() || null,
        availability_region: availability.availability_region,
        availability_forum: availability.availability_forum,
      });
      await onUploaded();
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to upload template');
    } finally {
      setIsSaving(false);
    }
  }, [availability, description, file, onOpenChange, onUploaded, title, whenToUse]);

  const canSubmit = !!file && isTemplateMetadataValid(description, whenToUse);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload firm template</DialogTitle>
          <DialogDescription>
            Add a reusable Word template available across firm matters.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TemplateFormFields
            fileInputId={fileInputId}
            titleInputId={titleInputId}
            descriptionInputId={descriptionInputId}
            whenToUseInputId={whenToUseInputId}
            title={title}
            description={description}
            whenToUse={whenToUse}
            availability={availability}
            file={file}
            onFileChange={handleFileChange}
            onTitleChange={(value) => {
              setTitleTouched(true);
              setTitle(value);
            }}
            onDescriptionChange={setDescription}
            onWhenToUseChange={setWhenToUse}
            onAvailabilityChange={setAvailability}
            disabled={isSaving}
            showFile
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" text="Cancel" onClick={() => onOpenChange(false)} disabled={isSaving} />
          <Button text="Upload" loadingText="Uploading" loading={isSaving} disabled={!canSubmit} onClick={handleSubmit} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AdminTemplateEditDialog = ({
  onOpenChange,
  onUpdated,
  open,
  template,
}: {
  onOpenChange: (open: boolean) => void;
  onUpdated: (template: DraftDocumentTemplate) => Promise<void>;
  open: boolean;
  template: DraftDocumentTemplate;
}) => {
  const titleInputId = React.useId();
  const descriptionInputId = React.useId();
  const whenToUseInputId = React.useId();
  const [title, setTitle] = React.useState(template.title);
  const [description, setDescription] = React.useState(template.description ?? '');
  const [whenToUse, setWhenToUse] = React.useState(template.when_to_use ?? '');
  const [availability, setAvailability] = React.useState<TemplateAvailabilityValue>({
    availability_region: template.availability_region,
    availability_forum: template.availability_forum,
  });
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setTitle(template.title);
    setDescription(template.description ?? '');
    setWhenToUse(template.when_to_use ?? '');
    setAvailability({
      availability_region: template.availability_region,
      availability_forum: template.availability_forum,
    });
    setIsSaving(false);
  }, [open, template]);

  const handleSubmit = React.useCallback(async () => {
    if (!isTemplateMetadataValid(description, whenToUse)) {
      toast.error('Add a description and a matcher prompt of at least 50 characters');
      return;
    }
    setIsSaving(true);
    try {
      const updated = await updateAdminDraftDocumentTemplate(template.template_guid, {
        title: title.trim(),
        description: description.trim() || null,
        when_to_use: whenToUse.trim() || null,
        availability_region: availability.availability_region,
        availability_forum: availability.availability_forum,
      });
      await onUpdated(updated);
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to update template');
    } finally {
      setIsSaving(false);
    }
  }, [availability, description, onOpenChange, onUpdated, template.template_guid, title, whenToUse]);

  const canSubmit = !!title.trim() && isTemplateMetadataValid(description, whenToUse);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure firm template</DialogTitle>
          <DialogDescription>
            Update how this template appears to users and Case Partner.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <TemplateFormFields
            titleInputId={titleInputId}
            descriptionInputId={descriptionInputId}
            whenToUseInputId={whenToUseInputId}
            title={title}
            description={description}
            whenToUse={whenToUse}
            availability={availability}
            onTitleChange={setTitle}
            onDescriptionChange={setDescription}
            onWhenToUseChange={setWhenToUse}
            onAvailabilityChange={setAvailability}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" text="Cancel" onClick={() => onOpenChange(false)} disabled={isSaving} />
          <Button text="Save" loadingText="Saving" loading={isSaving} disabled={!canSubmit} onClick={handleSubmit} />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export function TemplatesTab() {
  const searchInputId = React.useId();
  const [query, setQuery] = React.useState('');
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [templateToEdit, setTemplateToEdit] = React.useState<DraftDocumentTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = React.useState<DraftDocumentTemplate | null>(null);
  const [downloadingTemplateGuid, setDownloadingTemplateGuid] = React.useState<string | null>(null);
  const [deletingTemplateGuid, setDeletingTemplateGuid] = React.useState<string | null>(null);
  const {
    templates: tenantTemplates,
    isLoading: isLoadingTemplates,
    mutate: mutateTenantTemplates,
  } = useAdminDraftDocumentTemplates({
    query,
    limit: 200,
    enabled: true,
    scopes: ['tenant'],
  });

  const handleUploaded = React.useCallback(async () => {
    await mutateTenantTemplates();
    toast.success('Template uploaded');
  }, [mutateTenantTemplates]);

  const handleUpdated = React.useCallback(async (updatedTemplate: DraftDocumentTemplate) => {
    await mutateTenantTemplates(
      (current) => current
        ? {
          ...current,
          templates: current.templates.map((template) => (
            template.template_guid === updatedTemplate.template_guid ? updatedTemplate : template
          )),
        }
        : current,
      { revalidate: false },
    );
    void mutateTenantTemplates();
    setTemplateToEdit(null);
    toast.success('Template updated');
  }, [mutateTenantTemplates]);

  const handleDownload = React.useCallback(async (template: DraftDocumentTemplate) => {
    setDownloadingTemplateGuid(template.template_guid);
    try {
      await downloadAdminDraftDocumentTemplate(template);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download template');
    } finally {
      setDownloadingTemplateGuid(null);
    }
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!templateToDelete) return;
    const template = templateToDelete;
    setDeletingTemplateGuid(template.template_guid);
    try {
      await deleteAdminDraftDocumentTemplate(template.template_guid);
      await mutateTenantTemplates(
        (current) => current
          ? {
            ...current,
            templates: current.templates.filter((item) => item.template_guid !== template.template_guid),
          }
          : current,
        { revalidate: false },
      );
      void mutateTenantTemplates();
      setTemplateToDelete(null);
      toast.success('Template deleted');
    } catch (error) {
      toast.error(apiErrorMessage(error) || 'Failed to delete template');
    } finally {
      setDeletingTemplateGuid(null);
    }
  }, [mutateTenantTemplates, templateToDelete]);

  return (
    <AdminTabContainer>
      <section className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Firm templates</h2>
            <p className="text-sm text-slate-500">Available across this firm&apos;s matters for drafting.</p>
          </div>
          <Button
            icon={Plus}
            text="Firm template"
            variant="outline"
            onClick={() => setUploadOpen(true)}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full max-w-md">
            <label htmlFor={searchInputId} className="sr-only">Search templates</label>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              id={searchInputId}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search templates"
              className="h-10 pl-9"
            />
          </div>
        </div>

        {isLoadingTemplates ? (
          <div className="flex h-56 items-center justify-center">
            <Loading />
          </div>
        ) : (
          <AdminTemplateTable
            deletingTemplateGuid={deletingTemplateGuid}
            downloadingTemplateGuid={downloadingTemplateGuid}
            emptyTitle="No firm templates found."
            onDelete={setTemplateToDelete}
            onDownload={(template) => {
              void handleDownload(template);
            }}
            onEdit={setTemplateToEdit}
            templates={tenantTemplates}
          />
        )}
      </section>

      <AdminTemplateUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={handleUploaded}
      />
      {templateToEdit ? (
        <AdminTemplateEditDialog
          open={!!templateToEdit}
          template={templateToEdit}
          onOpenChange={(open) => {
            if (!open) setTemplateToEdit(null);
          }}
          onUpdated={handleUpdated}
        />
      ) : null}
      <AlertDialog open={!!templateToDelete} onOpenChange={(open) => {
        if (!open) setTemplateToDelete(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              {templateToDelete?.scope === 'system'
                ? `This will remove "${templateToDelete.title}" from Crimson system templates in every firm.`
                : `This will remove "${templateToDelete?.title}" from firm draft templates.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingTemplateGuid}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={!templateToDelete || deletingTemplateGuid === templateToDelete.template_guid}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
            >
              {deletingTemplateGuid === templateToDelete?.template_guid ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminTabContainer>
  );
}
